import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { DEFAULT_CONTENT } from "@/data/siteContent";

const ContentContext = createContext(null);

const CACHE_KEY = "kayama_content_v1";

function readCache() {
    try {
        const raw = localStorage.getItem(CACHE_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === "object") return parsed;
    } catch {}
    return null;
}

function writeCache(data) {
    try {
        localStorage.setItem(CACHE_KEY, JSON.stringify(data));
    } catch {}
}

// Deep merge so missing keys fall back to defaults.
// Type-strict: when base and override disagree in shape (object vs array vs primitive),
// the base's shape wins to keep the editor resilient to malformed persisted documents.
function deepMerge(base, override) {
    if (override === undefined || override === null) return base;
    const baseIsArr = Array.isArray(base);
    const overIsArr = Array.isArray(override);
    if (baseIsArr && overIsArr) return override;
    if (baseIsArr !== overIsArr) return base;
    const baseIsObj = base && typeof base === "object";
    const overIsObj = override && typeof override === "object";
    if (baseIsObj && overIsObj) {
        const out = { ...base };
        for (const k of Object.keys(override)) {
            out[k] = k in base ? deepMerge(base[k], override[k]) : override[k];
        }
        return out;
    }
    if (baseIsObj !== overIsObj) return base;
    return override;
}

export function ContentProvider({ children }) {
    // Hydrate immediately from localStorage cache to avoid the "flash of default content"
    // on subsequent visits. Falls back to DEFAULT_CONTENT for first-time visitors.
    const hadCache = typeof window !== "undefined" && readCache() !== null;
    const [content, setContent] = useState(() => {
        const cached = readCache();
        return cached ? deepMerge(DEFAULT_CONTENT, cached) : DEFAULT_CONTENT;
    });
    const [loaded, setLoaded] = useState(false);
    // Show a splash on the very first visit only (no cache) until first fetch resolves.
    const [splash, setSplash] = useState(!hadCache);

    const refresh = useCallback(async () => {
        try {
            const { data } = await api.get("/content");
            if (data && typeof data === "object") {
                const merged = deepMerge(DEFAULT_CONTENT, data);
                setContent(merged);
                writeCache(data);
                return data;
            }
        } catch (e) {
            // keep current state (cached or defaults)
        } finally {
            setLoaded(true);
        }
        return null;
    }, []);

    useEffect(() => {
        refresh().finally(() => setSplash(false));
        // Safety: never show splash longer than 2.5s
        const t = setTimeout(() => setSplash(false), 2500);
        // Re-fetch when the user returns to this tab so admin updates are reflected
        // without forcing a manual reload of open public pages.
        const onFocus = () => refresh();
        const onVisibility = () => {
            if (document.visibilityState === "visible") refresh();
        };
        window.addEventListener("focus", onFocus);
        document.addEventListener("visibilitychange", onVisibility);
        return () => {
            clearTimeout(t);
            window.removeEventListener("focus", onFocus);
            document.removeEventListener("visibilitychange", onVisibility);
        };
    }, [refresh]);

    return (
        <ContentContext.Provider value={{ content, loaded, refresh, setContent }}>
            {splash && (
                <div
                    aria-hidden="true"
                    style={{
                        position: "fixed",
                        inset: 0,
                        zIndex: 9998,
                        background: "#FAF8F5",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexDirection: "column",
                        gap: 16,
                        transition: "opacity 0.4s ease",
                    }}
                >
                    <img
                        src="https://customer-assets.emergentagent.com/job_ff078f22-bc2b-467a-b845-6483b385dad8/artifacts/c2lq75mw_image.png"
                        alt=""
                        style={{ width: 140, height: "auto", opacity: 0.9 }}
                    />
                    <div style={{
                        width: 32, height: 32, borderRadius: "50%",
                        border: "2px solid #E8E2D9",
                        borderTopColor: "#D81B60",
                        animation: "kayama-spin 0.8s linear infinite",
                    }} />
                    <style>{`@keyframes kayama-spin { to { transform: rotate(360deg); } }`}</style>
                </div>
            )}
            {children}
        </ContentContext.Provider>
    );
}

export function useContent() {
    const ctx = useContext(ContentContext);
    if (!ctx) throw new Error("useContent must be inside ContentProvider");
    return ctx.content;
}

export function useContentCtx() {
    const ctx = useContext(ContentContext);
    if (!ctx) throw new Error("useContentCtx must be inside ContentProvider");
    return ctx;
}
